import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [resendSuccessMessage, setResendSuccessMessage] = useState<string>('');
  const [resendLoading, setResendLoading] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return { num1, num2, answer: num1 + num2 };
  };
  
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const state = location.state as { message?: string; email?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
    }
    if (state?.email) {
      setPendingVerificationEmail(state.email);
      setFormData((prev) => ({ ...prev, email: state.email || prev.email }));
    }
  }, [location.state]);

  const handleResendVerification = async () => {
    const emailToUse = pendingVerificationEmail || formData.email;
    if (!emailToUse) {
      setError('Please enter your email to resend verification.');
      return;
    }

    setResendLoading(true);
    setError('');
    setResendSuccessMessage('');

    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.post(`${apiBaseUrl}/auth/resend-verification`, {
        email: emailToUse
      });

      setResendSuccessMessage(response.data.message || 'Verification email sent. Please check your inbox.');
    } catch (resendError: any) {
      setError(resendError.response?.data?.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSocialLogin = async (provider: string) => {
    setError('');
    setLoading(true);
    
    try {
      // For now, show a message that social login is coming soon
      // In production, you would implement actual OAuth flows
      setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login is coming soon! Please use email/password for now.`);
      
      // Example implementation for Google OAuth (would need proper setup)
      if (provider === 'google') {
        // This would integrate with Google Identity Services
        console.log('Google login clicked');
        // window.google?.accounts.id.prompt();
      }
    } catch (err) {
      setError('Social login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setResendSuccessMessage('');
    
    // Validate captcha
    if (parseInt(captchaAnswer) !== captcha.answer) {
      setError('Please solve the math problem correctly');
      setCaptcha(generateCaptcha()); // Generate new captcha
      setCaptchaAnswer('');
      return;
    }
    
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Small delay to ensure auth state is fully updated
        setTimeout(() => {
          navigate('/app/dashboard', { replace: true });
        }, 100);
      } else {
        setError(result.message);
        if (result.code === 'EMAIL_NOT_VERIFIED') {
          setPendingVerificationEmail(formData.email);
        }
        // Generate new captcha on failed login attempt
        setCaptcha(generateCaptcha());
        setCaptchaAnswer('');
      }
    } catch (error) {
      setError('An unexpected error occurred');
      setCaptcha(generateCaptcha());
      setCaptchaAnswer('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center public-page" 
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative'
      }}
    >
      <div 
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }}
      />
      
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col lg={5} md={7} sm={9}>
            <Card className="border-0 shadow-lg" style={{ backdropFilter: 'blur(10px)' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <div className="mb-3">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <i className="fas fa-trophy text-white" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">Welcome Back!</h2>
                  <p className="text-muted mb-0">Sign in to continue earning rewards</p>
                </div>

                {error && (
                  <Alert variant="danger" className="border-0 bg-danger bg-opacity-10 text-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </Alert>
                )}

                {infoMessage && (
                  <Alert variant="info" className="border-0 bg-info bg-opacity-10 text-info">
                    <i className="fas fa-info-circle me-2"></i>
                    {infoMessage}
                  </Alert>
                )}

                {resendSuccessMessage && (
                  <Alert variant="success" className="border-0 bg-success bg-opacity-10 text-success">
                    <i className="fas fa-check-circle me-2"></i>
                    {resendSuccessMessage}
                  </Alert>
                )}

                {/* Social Login Section */}
                {/* <div className="social-login mb-4">
                  <div className="text-center mb-3">
                    <small className="text-muted fw-medium">Sign in with your social account</small>
                  </div>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Button 
                        variant="outline-danger" 
                        className="w-100 social-btn py-2"
                        onClick={() => handleSocialLogin('google')}
                        disabled={loading}
                      >
                        <i className="fab fa-google me-2"></i>
                        Google
                      </Button>
                    </Col>
                    <Col xs={6}>
                      <Button 
                        variant="outline-primary" 
                        className="w-100 social-btn py-2"
                        onClick={() => handleSocialLogin('facebook')}
                        disabled={loading}
                      >
                        <i className="fab fa-facebook-f me-2"></i>
                        Facebook
                      </Button>
                    </Col>
                  </Row>
                  <Row className="g-2 mt-1">
                    <Col xs={6}>
                      <Button 
                        variant="outline-info" 
                        className="w-100 social-btn py-2"
                        onClick={() => handleSocialLogin('twitter')}
                        disabled={loading}
                      >
                        <i className="fab fa-twitter me-2"></i>
                        Twitter
                      </Button>
                    </Col>
                    <Col xs={6}>
                      <Button 
                        variant="outline-dark" 
                        className="w-100 social-btn py-2"
                        onClick={() => handleSocialLogin('github')}
                        disabled={loading}
                      >
                        <i className="fab fa-github me-2"></i>
                        GitHub
                      </Button>
                    </Col>
                  </Row>
                  <div className="divider my-4">
                    <hr />
                    <span className="divider-text bg-white px-3 text-muted small">OR CONTINUE WITH EMAIL</span>
                  </div>
                </div> */}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">Email Address</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="border-0 bg-light">
                        <i className="fas fa-envelope text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email address"
                        required
                        disabled={loading}
                        className="border-0 bg-light py-3"
                        style={{ fontSize: '1rem' }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="border-0 bg-light">
                        <i className="fas fa-lock text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        required
                        disabled={loading}
                        className="border-0 bg-light py-3"
                        style={{ fontSize: '1rem' }}
                      />
                      <InputGroup.Text 
                        className="border-0 bg-light"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>

                  {/* Math Captcha */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">
                      Security Verification
                    </Form.Label>
                    <div className="d-flex align-items-center mb-2">
                      <div 
                        className="captcha-display p-3 bg-light border rounded me-3"
                        style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          minWidth: '120px',
                          textAlign: 'center',
                          backgroundColor: '#f8f9fa',
                          border: '2px dashed #dee2e6'
                        }}
                      >
                        {captcha.num1} + {captcha.num2} = ?
                      </div>
                      <Button 
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setCaptcha(generateCaptcha());
                          setCaptchaAnswer('');
                        }}
                        disabled={loading}
                        title="Generate new math problem"
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                    <InputGroup>
                      <InputGroup.Text className="border-0 bg-light">
                        <i className="fas fa-calculator text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        placeholder="Enter the answer"
                        required
                        disabled={loading}
                        className="border-0 bg-light py-3"
                        style={{ fontSize: '1rem' }}
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      <i className="fas fa-shield-alt me-1"></i>
                      Solve the math problem to verify you're human
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2 mb-4">
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-lg border-0 fw-semibold py-3"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '1.1rem'
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                          Signing In...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-in-alt me-2"></i>
                          Sign In
                        </>
                      )}
                    </Button>
                    {pendingVerificationEmail && (
                      <Button
                        variant="outline-primary"
                        className="fw-semibold py-2"
                        onClick={handleResendVerification}
                        disabled={resendLoading || loading}
                      >
                        {resendLoading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                            Sending Verification...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-2"></i>
                            Resend Verification Email
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  <div className="text-center">
                    <div className="mb-3">
                      <Link 
                        to="/forgot-password" 
                        className="text-decoration-none"
                        style={{ color: '#667eea' }}
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    <p className="text-muted mb-0">
                      Don't have an account?{' '}
                      <Link 
                        to="/register" 
                        className="text-decoration-none fw-semibold"
                        style={{ color: '#667eea' }}
                      >
                        Create Account
                      </Link>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;